import { useState, useRef, useEffect } from 'react'

export function MultiSelectDropdown({
  options, selected, onChange, placeholder, widthClass = 'w-40',
}: {
  options: { value: string; label: string }[]
  selected: string[]
  onChange: (next: string[]) => void
  placeholder: string
  widthClass?: string
}) {
  const [open, setOpen] = useState(false)
  const [panelStyle, setPanelStyle] = useState<{ top?: number; bottom?: number; left: number; minWidth: number; maxHeight: number } | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const openPanel = () => {
    const rect = btnRef.current?.getBoundingClientRect()
    if (!rect) { setOpen(true); return }
    const margin = 8
    const spazioSotto = window.innerHeight - rect.bottom - margin
    const spazioSopra = rect.top - margin
    // Se sotto il bottone non c'è spazio sufficiente (es. bottone in fondo
    // a un modal), il pannello si apre sopra invece che essere tagliato dal
    // bordo dello schermo senza modo di raggiungerne il fondo.
    if (spazioSotto < 150 && spazioSopra > spazioSotto) {
      setPanelStyle({ bottom: window.innerHeight - rect.top + 4, left: rect.left, minWidth: rect.width, maxHeight: Math.min(256, spazioSopra) })
    } else {
      setPanelStyle({ top: rect.bottom + 4, left: rect.left, minWidth: rect.width, maxHeight: Math.min(256, spazioSotto) })
    }
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    // position: fixed è ancorato al viewport, non al contenitore scrollabile
    // (es. un modal con overflow-y-auto): se quel contenitore scorre mentre
    // il pannello è aperto, il pannello resterebbe fermo mentre il bottone
    // si sposta sotto. Più semplice e prevedibile chiuderlo allo scroll — ma
    // SOLO per lo scroll di un antenato esterno: lo scroll del pannello
    // stesso (per scorrere l'elenco) non deve chiuderlo.
    const onScroll = (e: Event) => {
      if (panelRef.current && panelRef.current.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open])

  const toggleValue = (value: string) => {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value])
  }

  const label = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? (options.find(o => o.value === selected[0])?.label ?? selected[0])
      : `${selected.length} selezionati`

  return (
    <div ref={ref} className="relative">
      <button
        ref={btnRef}
        type="button"
        className={`select ${widthClass} text-left flex items-center justify-between`}
        onClick={() => (open ? setOpen(false) : openPanel())}
      >
        <span className="truncate">{label}</span>
        <span className="ml-1 text-xs opacity-50">▾</span>
      </button>
      {open && panelStyle && (
        <div
          ref={panelRef}
          className="fixed bg-white border rounded-lg shadow-lg py-1 overflow-y-auto"
          style={{
            top: panelStyle.top, bottom: panelStyle.bottom, left: panelStyle.left,
            minWidth: panelStyle.minWidth, maxHeight: panelStyle.maxHeight,
            borderColor: 'var(--border)', zIndex: 1100,
          }}
        >
          <label className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold cursor-pointer hover:bg-slate-50" style={{ color: 'var(--signal)' }}>
            <input
              type="checkbox"
              checked={selected.length === options.length && options.length > 0}
              onChange={() => onChange(selected.length === options.length ? [] : options.map(o => o.value))}
            />
            {selected.length === options.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
          </label>
          <div className="border-t my-1" style={{ borderColor: 'var(--border)' }} />
          {options.map(o => (
            <label key={o.value} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50 whitespace-nowrap">
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggleValue(o.value)} />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}
